import { Component, OnInit } from '@angular/core';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ExtractsService } from '../../services/extracts-service/extracts.service';
import { DatePipe } from '@angular/common';
import { MessageService } from 'primeng/api';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { NgIf, NgForOf } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-extract-client',
  standalone: true,
  imports: [
    Breadcrumb,
    ReactiveFormsModule,
    FormsModule,
    NgIf, NgForOf,
    ButtonModule,
    ToastModule,
    TableModule,
    DropdownModule,
    DatePickerModule
  ],
  providers: [DatePipe, MessageService],
  templateUrl: './extract-client.component.html',
  styleUrls: ['./extract-client.component.scss']
})
export class ExtractClientComponent implements OnInit {
  breadcrumbHome = { icon: 'pi pi-database', routerLink: '/extract-client' };
  breadcrumbItems = [{ label: 'Rapports Clients' }];

  // DO
  doList: { label: string; value: string }[] = [];
  filteredDoList: { label: string; value: string }[] = [];

  doSearchText: string = '';
  modelesParDo: Record<string, string> = {};

  // Dates
  minDate: Date;
  maxDate: Date;
  today: Date;
  loadingPreview: boolean = false;
  isLoading: boolean = false;

  // Preview
  previewData: any[] = [];
  previewColumns: string[] = [];
  showPreviewTable: boolean = false;
  totalRecords: number = 0;
  exporting: boolean = false;

  private _selectedDo: string | null = null;
  private _startDate: Date | undefined;
  private _endDate: Date | undefined;

  constructor(
    private extractsService: ExtractsService,
    private messageService: MessageService,
    private datePipe: DatePipe
  ) {
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);
     this.minDate = new Date(2024, 0, 1);
    this.maxDate = new Date(this.today);
    this.maxDate.setDate(this.today.getDate() - 1);
  }

  ngOnInit(): void {
    this.loadDoList();
  }

  get selectedDo(): string | null {
    return this._selectedDo;
  }

  set selectedDo(value: string | null) {
    this._selectedDo = value;
    this.loadPreviewIfReady();
  }

  get startDate(): Date | undefined {
    return this._startDate;
  }

  set startDate(value: Date | undefined) {
    this._startDate = value;
    this.loadPreviewIfReady();
  }

  get endDate(): Date | undefined {
    return this._endDate;
  }

  set endDate(value: Date | undefined) {
    this._endDate = value;
    this.loadPreviewIfReady();
  }

  validateDates(): boolean {
    if (!this.startDate || !this.endDate) {
      return false;
    }
    const start = new Date(this.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.endDate);
    end.setHours(0, 0, 0, 0);
    return end >= start && end <= this.maxDate;
  }

  private async loadDoList(): Promise<void> {
    this.isLoading = true;
    try {
      const doData = await this.extractsService.list_do_client().toPromise();

      if (Array.isArray(doData)) {
        this.doList = doData.map((item: any) => {
          if (Array.isArray(item) && item.length >= 2) {
            const doName = item[0];
            const modele = item[1] || 'DEFAULT_MODEL';
            this.modelesParDo[doName] = modele;
            return {
              label: doName,
              value: doName
            };
          }
          const label = item.toString();
          return {
            label,
            value: label
          };
        });
        this.filteredDoList = [...this.doList];
      } else {
        this.doList = [];
        this.filteredDoList = [];
      }
    } catch (error) {
      console.error('Erreur lors du chargement des DO', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de charger la liste des DO'
      });
      this.doList = [];
      this.filteredDoList = [];
    } finally {
      this.isLoading = false;
    }
  }

  filterDoList(): void {
    if (!this.doList || this.doList.length === 0) {
      this.filteredDoList = [];
      return;
    }

    const searchText = (this.doSearchText || '').toString().toLowerCase().trim();
    this.filteredDoList = this.doList.filter(doItem =>
      doItem.label.toLowerCase().includes(searchText)
    );
  }

  private loadPreviewIfReady(): void {
    if (this.selectedDo && this.validateDates()) {
      this.loadPreview();
    } else {
      this.showPreviewTable = false;
    }
  }

  private loadPreview(): void {
    this.loadingPreview = true;
    this.showPreviewTable = false;

    const formattedStartDate = this.datePipe.transform(this.startDate, 'dd/MM/yy') || '';
    const formattedEndDate = this.datePipe.transform(this.endDate, 'dd/MM/yy') || '';
    const modele = this.modelesParDo[this.selectedDo!] || 'DEFAULT_MODEL';

    this.extractsService.preview_data_by_modele(
      this.selectedDo!,
      modele,
      formattedStartDate,
      formattedEndDate
    ).subscribe({
      next: (response) => {
        this.previewData = response.data;

        if (this.previewData.length > 0) {
          this.previewColumns = Object.keys(this.previewData[0]);
          this.totalRecords = response.metadata.total_rows;
          this.showPreviewTable = true;
        } else {
          this.showPreviewTable = false;
          this.messageService.add({
            severity: 'info',
            summary: 'Information',
            detail: 'Aucune donnée disponible pour les critères sélectionnés'
          });
          this.resetAfterAction(false);
        }
      },
      error: (error) => {
        console.error('Erreur lors du chargement de la prévisualisation', error);
        this.showPreviewTable = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors du chargement de la prévisualisation'
        });
        this.resetAfterAction(false);
      },
      complete: () => {
        this.loadingPreview = false;
      }
    });
  }

  exportClientReport(): void {
    if (this.exporting) return;

    this.exporting = true;

    if (!this.selectedDo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner un DO'
      });
      this.exporting = false;
      return;
    }

    if (!this.validateDates()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attention',
        detail: 'Veuillez sélectionner des dates valides'
      });
      this.exporting = false;
      return;
    }

    this.isLoading = true;

    const formattedStartDate = this.datePipe.transform(this.startDate, 'dd/MM/yy') || '';
    const formattedEndDate = this.datePipe.transform(this.endDate, 'dd/MM/yy') || '';
    const modele = this.modelesParDo[this.selectedDo] || 'DEFAULT_MODEL';

    this.extractsService.get_data_by_modele(
      this.selectedDo,
      modele,
      formattedStartDate,
      formattedEndDate,
      true
    ).subscribe({
      next: (data: Blob) => {
        const filename = `rapport_client_${this.selectedDo}_${formattedStartDate}_${formattedEndDate}.csv`;
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();

        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: 'Le rapport a été téléchargé avec succès'
        });
        this.resetAfterAction(false);
      },
      error: (error) => {
        console.error('Erreur lors de l\'export', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de l\'export du rapport'
        });
        this.resetAfterAction(false);
      },
      complete: () => {
        this.isLoading = false;
        this.exporting = false;
      }
    });
  }

  resetFilters() {

    this.startDate = undefined;
    this.endDate = undefined;
    this.doSearchText = '';
    this.previewData = [];
    this.showPreviewTable = false;

  }

  resetAfterAction(resetDo: boolean = false) {

    this.exporting = false;
    this.loadingPreview = false;
    this.isLoading = false;
    this.startDate = undefined;
    this.endDate = undefined;
    this.previewData = [];
    this.previewColumns = [];
    this.showPreviewTable = false;
    this.totalRecords = 0;
    this.filteredDoList = this.doList.length > 0 ? [...this.doList] : [];
    this.doSearchText = '';
    if (resetDo) {
      this.selectedDo = null;
    }
  }

  onDoChange() {
    this.startDate = undefined;
    this.endDate = undefined;
    this.previewData = [];
    this.showPreviewTable = false;
  }
}
